import { create } from 'zustand';
import { 
  browserLocalPersistence,
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  setPersistence,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { getAcquisitionAttribution } from '@/lib/acquisition';
import { getAuthActionCodeSettings } from '@/lib/authActionSettings';
import type { IdentityVerificationStatus, UserRole, UserStatus } from '@kulum/shared';
import type { ImpersonationContext } from '@/lib/impersonation';

// Auth user type (from Firebase)
export interface AuthUser {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  displayName: string | null;
  photoURL: string | null;
}

// Extended user data (from Firestore)
export interface UserData {
  uid: string;
  email: string;
  displayName: string;
  role: UserRole;
  status: UserStatus;
  identityVerified: boolean;
  identityVerificationStatus: IdentityVerificationStatus;
  rejectedUntil?: Date;
  iconUrl?: string;
  createdAt: Date;
  updatedAt: Date;
}

interface AuthState {
  user: AuthUser | null;
  userData: UserData | null;
  impersonation: ImpersonationContext | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  
  // Actions
  setUser: (user: AuthUser | null) => void;
  setUserData: (userData: UserData | null) => void;
  setImpersonation: (impersonation: ImpersonationContext | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Auth actions
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string, role: UserRole) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  fetchUserData: (uid: string) => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  userData: null,
  impersonation: null,
  isLoading: true,
  isInitialized: false,
  error: null,

  setUser: (user) => set({ user, isInitialized: true }),
  setUserData: (userData) => set({ userData }),
  setImpersonation: (impersonation) => set({ impersonation }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),

  signIn: async (email, password) => {
    try {
      set({ isLoading: true, error: null });
      await setPersistence(auth, browserLocalPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Update last login
      const userRef = doc(db, 'users', userCredential.user.uid);
      await setDoc(userRef, { lastLoginAt: serverTimestamp() }, { merge: true });
      
      // Fetch user data
      await get().fetchUserData(userCredential.user.uid);
    } catch (error: unknown) {
      const message = getFirebaseErrorMessage(error);
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  signUp: async (email, password, displayName, role) => {
    try {
      set({ isLoading: true, error: null });
      await setPersistence(auth, browserLocalPersistence);
      
      // Create auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const { user } = userCredential;
      
      // Update profile
      await updateProfile(user, { displayName });
      
      const initialStatus: UserStatus = 'pending';
      const acquisition = getAcquisitionAttribution();
      
      // Create user document
      const userRef = doc(db, 'users', user.uid);
      try {
        await setDoc(userRef, {
          email,
          displayName,
          role,
          status: initialStatus,
          identityVerified: false,
          identityVerificationStatus: 'none',
          acquisition: acquisition || null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } catch (firestoreError) {
        console.error('Firestore user document creation failed:', firestoreError);
        // Continue anyway - email verification is more important
      }
      
      // Create empty profile document
      const profileRef = doc(db, 'profiles', user.uid);
      try {
        await setDoc(profileRef, {
          userId: user.uid,
          basicInfo: {},
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } catch (firestoreError) {
        console.error('Firestore profile document creation failed:', firestoreError);
        // Continue anyway
      }
      
      // Send verification email
      try {
        await sendEmailVerification(user, getAuthActionCodeSettings('verifyEmail'));
      } catch (emailError) {
        console.error('Verification email failed:', emailError);
        // Don't throw - let user proceed to verification page
      }
      
      // Fetch user data
      await get().fetchUserData(user.uid);
    } catch (error: unknown) {
      console.error('signUp error:', error);
      const message = getFirebaseErrorMessage(error);
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  signOut: async () => {
    try {
      set({ isLoading: true, error: null });
      await firebaseSignOut(auth);
      set({ user: null, userData: null, impersonation: null });
    } catch (error: unknown) {
      const message = getFirebaseErrorMessage(error);
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  resetPassword: async (email) => {
    try {
      set({ isLoading: true, error: null });
      await sendPasswordResetEmail(auth, email, getAuthActionCodeSettings('resetPassword'));
    } catch (error: unknown) {
      const message = getFirebaseErrorMessage(error);
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  sendVerificationEmail: async () => {
    try {
      set({ isLoading: true, error: null });
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('ログインが必要です');
      }
      await sendEmailVerification(currentUser, getAuthActionCodeSettings('verifyEmail'));
    } catch (error: unknown) {
      console.error('sendVerificationEmail error:', error);
      const message = getFirebaseErrorMessage(error);
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  fetchUserData: async (uid) => {
    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const data = userSnap.data();
        let status = data.status as UserStatus;
        let email =
          typeof data.email === 'string'
            ? data.email
            : auth.currentUser?.uid === uid && auth.currentUser.email
              ? auth.currentUser.email
              : '';
        let rejectedUntil = data.rejectedUntil?.toDate?.() as Date | undefined;

        // Keep users.email aligned with Firebase Auth email (e.g. verifyBeforeUpdateEmail completion).
        const authEmail = auth.currentUser?.uid === uid ? auth.currentUser.email : null;
        if (authEmail && authEmail !== email) {
          try {
            await updateDoc(userRef, {
              email: authEmail,
              updatedAt: serverTimestamp(),
            });
            email = authEmail;
          } catch (updateError) {
            console.error('Failed to sync users.email from Auth:', updateError);
          }
        }

        // Keep users.status aligned with Firebase email verification.
        if (status === 'pending' && auth.currentUser?.emailVerified) {
          try {
            await updateDoc(userRef, {
              status: 'email_verified',
              updatedAt: serverTimestamp(),
            });
            status = 'email_verified';
          } catch (updateError) {
            console.error('Failed to sync email_verified status:', updateError);
          }
        }

        // Release rejected cooldown automatically after the deadline.
        if (
          status === 'rejected' &&
          rejectedUntil instanceof Date &&
          rejectedUntil.getTime() <= Date.now()
        ) {
          try {
            await updateDoc(userRef, {
              status: 'email_verified',
              rejectedUntil: null,
              updatedAt: serverTimestamp(),
            });
            status = 'email_verified';
            rejectedUntil = undefined;
          } catch (updateError) {
            console.error('Failed to release rejected cooldown:', updateError);
          }
        }

        const identityVerificationStatus: IdentityVerificationStatus =
          data.identityVerificationStatus === 'pending' ||
          data.identityVerificationStatus === 'verified' ||
          data.identityVerificationStatus === 'rejected'
            ? data.identityVerificationStatus
            : 'none';

        set({
          userData: {
            uid,
            email,
            displayName: data.displayName,
            role: data.role,
            status,
            identityVerified: data.identityVerified === true,
            identityVerificationStatus,
            rejectedUntil,
            iconUrl: data.iconUrl,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
          },
        });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  },
}));

// Firebase error message helper
function getFirebaseErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: string }).code;
    switch (code) {
      case 'auth/email-already-in-use':
        return 'このメールアドレスは既に登録されています';
      case 'auth/invalid-email':
        return 'メールアドレスの形式が正しくありません';
      case 'auth/operation-not-allowed':
        return 'この操作は許可されていません';
      case 'auth/weak-password':
        return 'パスワードは6文字以上で入力してください';
      case 'auth/user-disabled':
        return 'このアカウントは無効化されています';
      case 'auth/user-not-found':
        return 'メールアドレスまたはパスワードが間違っています';
      case 'auth/wrong-password':
        return 'メールアドレスまたはパスワードが間違っています';
      case 'auth/invalid-credential':
        return 'メールアドレスまたはパスワードが間違っています';
      case 'auth/too-many-requests':
        return 'ログイン試行回数が多すぎます。しばらく経ってから再度お試しください';
      case 'auth/network-request-failed':
        return 'ネットワークエラーが発生しました';
      default:
        return 'エラーが発生しました。もう一度お試しください';
    }
  }
  return 'エラーが発生しました。もう一度お試しください';
}
